/**
 * i18n utility for the Inhab™ Control application.
 *
 * Usage:
 *   import { t } from 'utils/i18n'
 *
 *   // Simple key access (dot-notation string)
 *   t('login.loginButton')           // → 'Login'
 *
 *   // Calling a function value directly via the `en` object
 *   import en from 'locales/en'
 *   en.profile.welcome('John')       // → 'Welcome, John'
 *
 * The `t` helper performs safe deep key lookup so unknown keys return an
 * empty string (rather than throwing) and TypeScript consumers benefit from
 * IntelliSense via the `en` object directly.
 */

import en from "locales/en";

type DeepRecord = {
  [key: string]:
    | string
    | number
    | boolean
    | DeepRecord
    | ((...args: any[]) => string)
    | any[];
};

/**
 * Retrieve a translation string by dot-notation key.
 *
 * @param key  Dot-separated path, e.g. `'login.loginButton'`
 * @returns    The resolved string, or an empty string if not found.
 *
 * @example
 *   t('common.cancel')       // → 'Cancel'
 *   t('login.loginButton')   // → 'Login'
 */
export function t(key: string): string {
  const parts = key.split(".");
  let current: any = en;

  for (const part of parts) {
    if (current == null || typeof current !== "object") return "";
    current = current[part];
  }

  if (typeof current === "string") return current;
  if (typeof current === "number") return String(current);
  return "";
}

/**
 * Direct access to the full translation object.
 * Use this for function-valued translations and typed access.
 *
 * @example
 *   translations.profile.welcome('Alice')        // → 'Welcome, Alice'
 *   translations.home.greeting('Bob')            // → 'Hello Bob'
 */
export const translations = en;

export default t;
